// "I'm away these dates."
//
// Taylor: "Could we add a feature to easily enter time off or block off
// specific hours directly from the availability section? ... if I enter the
// dates of an upcoming trip, the system would mark me unavailable for those
// dates, then automatically resume my regular schedule afterward."
//
// All of that already worked. The pattern is never touched — time off is a
// separate record that the slot engine subtracts, so the regular schedule
// resumes on its own the day the trip ends, and the conflict check has named
// the riders affected since the pattern builder shipped.
//
// What did not work was SAYING it. The only way in was: open the calendar,
// tap a day, tap the pencil, scroll to "Mark time off", and set a through
// date — four taps that begin by picking a day, which is a strange way to
// enter "the 3rd to the 10th of November". This is the same machinery with
// the range asked for first, which is the order the sentence arrives in.
//
// It deliberately does NOT re-implement the conflict check. Submitting runs
// the same impact query the day panel runs, and the same confirmation lists
// the riders by name — one implementation of "who does this disturb", because
// two would eventually disagree about somebody's Tuesday.

import { useState } from 'react';
import TimeSelect from '../ui/TimeSelect';
import { fmtDate } from '../../lib/dates';

const REASONS = ['Show', 'Vacation', 'Sick', 'Appointment'];

function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function days(from, to) {
  const a = new Date(from + 'T00:00:00Z').getTime();
  const b = new Date(to + 'T00:00:00Z').getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86400000) + 1;
}

export default function TimeOffRange({ today, whose, busy, error, onSubmit, onClose }) {
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('');

  const backwards = to < from;
  const span = backwards ? 0 : days(from, to);
  const badHours = !allDay && startTime >= endTime;

  function submit(e) {
    e.preventDefault();
    if (backwards || badHours || busy) return;
    onSubmit({
      from,
      through: to,
      reason: reason.trim() || null,
      startTime: allDay ? null : startTime,
      endTime: allDay ? null : endTime,
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Time off"
        className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-xl overflow-hidden max-h-full overflow-y-auto"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-ink-100">
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-lg leading-snug">Time off</h2>
            <p className="text-sm text-ink-500">
              {whose ? `${whose} is` : 'You are'} unavailable for these dates. The usual schedule
              comes back on its own afterwards.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none shrink-0">
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">From</span>
              <input
                type="date"
                className="input"
                value={from}
                disabled={busy}
                onChange={(e) => {
                  const v = e.target.value;
                  setFrom(v);
                  // A range that runs backwards is almost always a half-typed
                  // one, so the end follows the start rather than sitting
                  // there as an error somebody has to go and fix.
                  if (v && to < v) setTo(v);
                }}
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">To</span>
              <input type="date" className="input" value={to} min={from} disabled={busy}
                onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>

          {!backwards && (
            <p className="text-xs text-ink-500">
              {span === 1
                ? fmtDate(from, 'dayMedium')
                : `${fmtDate(from, 'medium')} to ${fmtDate(to, 'medium')} — ${span} days`}
              {/* Where the regular schedule picks up again, said plainly. It is
                  the half of her question that the app can answer before she
                  commits to anything. */}
              {span >= 1 && (
                <span className="text-ink-400">
                  {' · '}back as usual on {fmtDate(addDays(to, 1), 'medium')}
                </span>
              )}
            </p>
          )}
          {backwards && <p className="text-sm text-rose-700">The last day is before the first.</p>}

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={allDay} disabled={busy}
              onChange={(e) => setAllDay(e.target.checked)} />
            <span>Away the whole day</span>
          </label>

          {!allDay && (
            <div>
              <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">
                Away between
              </span>
              <div className="flex items-end gap-3 flex-wrap">
                <TimeSelect label="From" value={startTime} onChange={setStartTime} disabled={busy} />
                <span className="pb-2 text-ink-400">to</span>
                <TimeSelect label="To" value={endTime} onChange={setEndTime} disabled={busy} />
              </div>
              {badHours && <p className="mt-1 text-sm text-rose-700">The end is before the start.</p>}
              {span > 1 && !badHours && (
                // Worth saying: part-day off over a stretch means those hours
                // on EVERY day in it, which is what a standing appointment
                // looks like and is not what a trip looks like.
                <p className="mt-1 text-xs text-ink-500">
                  Those hours on each of the {span} days.
                </p>
              )}
            </div>
          )}

          <div>
            <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">Reason</span>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  disabled={busy}
                  onClick={() => setReason(reason === r ? '' : r)}
                  className={
                    'px-2.5 py-1 rounded-full text-sm border ' +
                    (reason === r
                      ? 'bg-hunter text-white border-hunter'
                      : 'bg-white text-ink-700 border-ink-200 hover:border-ink-400')
                  }
                >
                  {r}
                </button>
              ))}
            </div>
            <input className="input" value={reason} disabled={busy} placeholder="Optional"
              onChange={(e) => setReason(e.target.value)} />
          </div>

          {error && <p className="text-sm text-rose-700">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-ink-100">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy || backwards || badHours}>
            {busy ? 'Checking…' : 'Mark it off'}
          </button>
        </div>
      </form>
    </div>
  );
}
